import { create } from 'zustand';

export interface VoiceDetails {
    name: string;
    languageCode: string;
    description: string;
    hasRights: boolean;
}

interface VoiceCloningState {
    // Step management
    currentStep: number;
    setCurrentStep: (step: number) => void;
    
    // Audio file management
    audioFile: File | null;
    audioUrl: string | null;
    audioDuration: number;
    setAudioFile: (file: File | null, url: string | null, duration: number) => void;
    
    // Voice details
    voiceDetails: VoiceDetails;
    setVoiceDetails: (details: Partial<VoiceDetails>) => void;

    // Preview state
    uploadedAudioUrl: string | null;
    setUploadedAudioUrl: (url: string | null) => void;
    
    // Validation helpers
    isStep1Valid: () => boolean;
    isStep2Valid: () => boolean;
    
    // Reset store
    resetStore: () => void;
}

const initialVoiceDetails: VoiceDetails = {
    name: '',
    languageCode: '',
    description: '',
    hasRights: false
};

export const useVoiceCloningStore = create<VoiceCloningState>((set, get) => ({
    // Initial state
    currentStep: 1,
    audioFile: null,
    audioUrl: null,
    audioDuration: 0,
    voiceDetails: initialVoiceDetails,
    uploadedAudioUrl: null,

    // Actions
    setCurrentStep: (step: number) => set({ currentStep: step }),
    
    setAudioFile: (file: File | null, url: string | null, duration: number) => 
        set({ audioFile: file, audioUrl: url, audioDuration: duration }),
    
    setVoiceDetails: (details: Partial<VoiceDetails>) =>
        set((state) => ({
            voiceDetails: { ...state.voiceDetails, ...details }
        })),

    setUploadedAudioUrl: (url: string | null) => set({ uploadedAudioUrl: url }),
    
    // Validation helpers
    isStep1Valid: () => {
        const { audioFile, audioDuration } = get();
        return audioFile !== null && audioDuration >= 10;
    },
    
    isStep2Valid: () => {
        const { voiceDetails } = get();
        return voiceDetails.name.trim() !== '' && 
               voiceDetails.languageCode !== '' && 
               voiceDetails.hasRights;
    },
    
    // Reset store
    resetStore: () => set({
        currentStep: 1,
        audioFile: null,
        audioUrl: null,
        audioDuration: 0,
        voiceDetails: initialVoiceDetails,
        uploadedAudioUrl: null
    })
}));

